<script setup lang="ts">
import { computed } from 'vue';
import ChatMessageWrapper from '../ChatMessageWrapper.vue';
import ConnectOption from './ConnectOption.vue';
import type { ConnectOptionSelectMessage } from '@/common/bot/types';
import type { UserSelectedConnectOptionEvent } from '@/common/bot/types/events/external/external-events';
import { buildExternalEvent } from '@/common/bot/lib/event.factory';
import { ExternalBotEvent } from '@/common/bot/enum/ExternalBotEvent';
import { ConnectOptionType, type ConnectOptionKind } from '@/common/bot/enum';
import { getConnectOptionChannelLabel } from '@/common/connectOption.utils';
import { useConnectData } from './useConnectData';
import { useMessageActionsCustomisation } from '../../composables/useMessageActionsCustomisation';
import { analytics, COMPONENT_EVENTS } from '@/common/logs';
import { useI18n } from '@/plugins/i18n';
import { get } from '@/common/environment';

export interface ConnectOptionsMessageProps {
  /**
   * The message to render.
   */
  message: ConnectOptionSelectMessage;
  /**
   * The previous message/notice in the stack.
   */
  previous?: unknown;
  /**
   * The next message/notice in the stack.
   */
  next?: unknown;
}

interface ConnectOptionData {
  type: ConnectOptionKind;
  title: string;
  estimate?: string;
  disabled?: boolean;
}

const props = defineProps<ConnectOptionsMessageProps>();

const { t } = useI18n();
const openRequestResponseTime = get('defaultOpenRequestResponseTime') ?? 24;

const emit = defineEmits<{
  (e: 'external-event', event: UserSelectedConnectOptionEvent): void;
}>();

// Fetch connect data (queue position, agents online, wait times)
const connectDataProps = computed(() => ({
  showLiveChat: props.message.showLiveChat ?? true,
  showVoiceChat: props.message.showVoiceChat ?? true,
  showOpenRequest: props.message.showOpenRequest ?? true,
  isLandingEntry: props.message.isLandingEntry ?? false,
  selectedOption: props.message.selectedOption,
}));

const connectData = useConnectData(connectDataProps);

// Apply customisation
const customisation = useMessageActionsCustomisation();

const loadingStyle = computed(() => {
  const textColor = customisation.suggestionsTextColor.value;
  return {
    ...(textColor ? { color: textColor } : {}),
  };
});

const availableOptions = computed<ConnectOptionData[]>(() => {
  const options: ConnectOptionData[] = [];
  const isLandingEntry = props.message.isLandingEntry === true;

  // Add Live Chat option if available
  if (connectData.value.showLiveChat) {
    const chatWaitTime = isLandingEntry ? null : connectData.value.waitTimes.chat;
    options.push({
      type: ConnectOptionType.LIVE_CHAT,
      title: getConnectOptionChannelLabel(ConnectOptionType.LIVE_CHAT),
      estimate: chatWaitTime ? `~${chatWaitTime} min wait` : undefined,
    });
  }

  // Add Voice Call option if available (Twilio agent call)
  if (connectData.value.showCall && !isLandingEntry) {
    const callWaitTime = connectData.value.waitTimes.call;
    options.push({
      type: ConnectOptionType.VOICE_CALL,
      title: getConnectOptionChannelLabel(ConnectOptionType.VOICE_CALL),
      estimate: callWaitTime ? `~${callWaitTime} min wait` : undefined,
    });
  }

  // Landing Voice AI — separate from Twilio VOICE_CALL
  if (connectData.value.showCall && isLandingEntry) {
    options.push({
      type: ConnectOptionType.VOICE_AI,
      title: getConnectOptionChannelLabel(ConnectOptionType.VOICE_AI),
      disabled: connectData.value.isVoiceAiDisabled,
    });
  }

  // Add Follow Up option if ticketId is present
  if (props.message.ticketId) {
    options.push({
      type: ConnectOptionType.FOLLOW_UP,
      title: getConnectOptionChannelLabel(ConnectOptionType.FOLLOW_UP),
      estimate: t('page.connect.open_request.estimate', { hours: openRequestResponseTime }),
    });
  }

  const isOtherOptionAvailable = options.length > 0;

  // Add Open Request option if available
  if (connectData.value.showOpenRequest || !isOtherOptionAvailable) {
    options.push({
      type: ConnectOptionType.OPEN_REQUEST,
      title: getConnectOptionChannelLabel(ConnectOptionType.OPEN_REQUEST),
      estimate: t('page.connect.open_request.estimate', { hours: openRequestResponseTime }),
    });
  }

  // Cancel only when there's no follow-up (active conversation); with follow-up we don't need cancel
  if (!props.message.ticketId && !props.message.isLandingEntry) {
    options.push({
      type: ConnectOptionType.CANCEL,
      title: getConnectOptionChannelLabel(ConnectOptionType.CANCEL),
    });
  }

  return options;
});

const isSelectable = computed(() => {
  return !props.next;
});

const selectedOption = computed(() => {
  return props.message.selectedOption;
});

const getConnectOptionAnalyticsSuffix = (connectOption: ConnectOptionKind): string => {
  if (connectOption === ConnectOptionType.LIVE_CHAT) {
    return COMPONENT_EVENTS.CONNECT_OPTIONS_CHAT;
  }
  if (connectOption === ConnectOptionType.VOICE_CALL) {
    return COMPONENT_EVENTS.CONNECT_OPTIONS_CALL;
  }
  if (connectOption === ConnectOptionType.VOICE_AI) {
    return COMPONENT_EVENTS.CONNECT_OPTIONS_VOICE_AI;
  }
  if (connectOption === ConnectOptionType.OPEN_REQUEST) {
    return COMPONENT_EVENTS.CONNECT_OPTIONS_OPEN_REQUEST;
  }
  if (connectOption === ConnectOptionType.FOLLOW_UP) {
    return COMPONENT_EVENTS.CONNECT_OPTIONS_FOLLOW_UP;
  }
  return COMPONENT_EVENTS.CONNECT_OPTIONS;
};

const handleOptionSelect = (connectOption: ConnectOptionKind) => {
  if (!isSelectable.value) return;

  const selection = props.message.ticketId ?? props.message.id;
  analytics.trackClick({
    name: getConnectOptionAnalyticsSuffix(connectOption),
    ...(selection !== undefined && selection !== null ? { selection } : {}),
    metadata: {
      connectOption,
      messageId: props.message.id,
      ticketId: props.message.ticketId,
    },
  });

  const event = buildExternalEvent<UserSelectedConnectOptionEvent>({
    type: ExternalBotEvent.USER_SELECTED_CONNECT_OPTION,
    payload: {
      connectOption,
      ticketId: props.message.ticketId,
      message: props.message,
    },
  });

  emit('external-event', event);
};
</script>

<template>
  <ChatMessageWrapper :previous="previous" :next="next" :message="message">
    <template #actions>
      <div v-if="connectData.isLoading" class="connect-options">
        <div class="connect-options__loading theme-font-main" :style="loadingStyle">Loading...</div>
      </div>
      <div v-else class="connect-options">
        <div class="connect-options__list">
          <ConnectOption
            v-for="option in availableOptions"
            :key="option.type"
            :type="option.type"
            :title="option.title"
            :estimate="option.estimate"
            :disabled="option.disabled"
            :selected="option.type === selectedOption"
            @select="handleOptionSelect"
          />
        </div>
      </div>
    </template>
  </ChatMessageWrapper>
</template>

<style scoped>
:deep(.message-content) {
  width: 100%;
}

.connect-options {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.connect-options__loading {
  text-align: center;
  padding: 16px;
}

.connect-options__list {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.connect-options__list > :last-child:nth-child(odd) {
  grid-column: 1 / -1;
}
</style>
